import { useState } from "react";
import { NavLink } from "react-router-dom";
import { ENABLE_SALES_ADMIN } from "../config/flags";
import { useQueueCounts } from "../hooks/useQueueCounts";
import { useUserPermissions } from "../hooks/useUserPermissions";

export default function Sidebar() {
  const { counts, loading } = useQueueCounts();
  const { hasPermission, loading: permissionsLoading } = useUserPermissions();
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({
    tasks: false,
    appManager: false,
    labelingManager: false,
    aiManager: false,
    dataAnalytics: false,
    clientPortal: false,
    admin: false,
  });

  const toggleDropdown = (dropdown: string) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [dropdown]: !prev[dropdown]
    }));
  };

  if (permissionsLoading) {
    return (
      <div className="flex flex-col items-center bg-black text-white w-48 min-h-screen py-6">
        <img src="/pix-logo.png" alt="Pixano Logo" className="w-20 h-20 mb-8" style={{ objectFit: 'contain' }} />
        <div className="text-center text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center bg-black text-white w-48 min-h-screen py-6">
      <img src="/pix-logo.png" alt="Pixano Logo" className="w-20 h-20 mb-8" style={{ objectFit: 'contain' }} />
      
      {/* Dashboard */}
        <NavLink
        to="/dashboard"
          className={({ isActive }) =>
          `w-full px-4 py-2 mb-2 rounded transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
          }
        >
        Dashboard
        </NavLink>
      
      {/* WORK FLOW Section */}
      <div className="w-full px-4 py-2 mb-2">
        <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">WORK FLOW</div>

        {hasPermission('approvals') && (
          <NavLink
            to="/approvals"
            className={({ isActive }) =>
              `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
            }
          >
            <div className="flex items-center justify-between">
              <span>Task Approvals</span>
              {!loading && (
                <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full ml-2">
                  {counts.approvals}
                </span>
              )}
            </div>
          </NavLink>
        )}

        {hasPermission('labeling.workbench') && (
          <NavLink
            to="/labeling/workbench"
            className={({ isActive }) =>
              `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
            }
          >
            <div className="flex items-center justify-between">
              <span>Workbench</span>
              {!loading && (
                <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full ml-2">
                  {counts.workbench}
                </span>
              )}
            </div>
          </NavLink>
        )}

        {hasPermission('qa') && (
          <NavLink
            to="/qa"
            className={({ isActive }) =>
              `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
            }
          >
            <div className="flex items-center justify-between">
              <span>QA</span>
              {!loading && (
                <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full ml-2">
                  {counts.qa || 0}
                </span>
              )}
            </div>
          </NavLink>
        )}

        {hasPermission('labeling.review') && (
          <NavLink
            to="/labeling/review"
            className={({ isActive }) =>
              `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
            }
          >
            <div className="flex items-center justify-between">
              <span>Review</span>
              {!loading && (
                <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full ml-2">
                  {counts.review}
                </span>
              )}
            </div>
          </NavLink>
        )}

        {hasPermission('labeling.queue') && (
          <>
          <NavLink
            to="/schema-needed"
            className={({ isActive }) =>
              `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
            }
          >
            <div className="flex items-center justify-between">
              <span>Schema Needed</span>
              {!loading && (
                <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full ml-2">
                  {counts.schemaNeeded || 0}
                </span>
              )}
            </div>
          </NavLink>
          <NavLink
            to="/orphan-submissions"
            className={({ isActive }) =>
              `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
            }
          >
            Orphan Submissions
          </NavLink>
          </>
        )}
      </div>

      {/* VIDEO WORK FLOW Section */}
      <div className="w-full px-4 py-2 mb-2">
        <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">VIDEO WORK FLOW</div>

        {hasPermission('approvals') && (
          <NavLink
            to="/video-trimming"
            className={({ isActive }) =>
              `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
            }
          >
            <div className="flex items-center justify-between">
              <span>Video Trimming</span>
              {!loading && (
                <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full ml-2">
                  {counts.trimmingCount}
                </span>
              )}
            </div>
          </NavLink>
        )}

        {hasPermission('labeling.workbench') && (
          <NavLink
            to="/labeling/video-workbench"
            className={({ isActive }) =>
              `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
            }
          >
            <div className="flex items-center justify-between">
              <span>Video Workbench</span>
              {!loading && (
                <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full ml-2">
                  {counts.videoWorkbenchCount}
                </span>
              )}
            </div>
          </NavLink>
        )}
      </div>

      {/* REQUESTS Section */}
      <div className="w-full px-4 py-2 mb-2">
        <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">REQUESTS</div>

        {hasPermission('labeling.queue') && (
          <NavLink
            to="/dataset-requests"
            className={({ isActive }) =>
              `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
            }
          >
            <div className="flex items-center justify-between">
              <span>Dataset Requests</span>
              {!loading && counts.datasetRequestsCount > 0 && (
                <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full ml-2">
                  {counts.datasetRequestsCount}
                </span>
              )}
            </div>
          </NavLink>
        )}

        {hasPermission('labeling.queue') && (
          <NavLink
            to="/support-requests"
            className={({ isActive }) =>
              `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
            }
          >
            <div className="flex items-center justify-between">
              <span>Support Requests</span>
              {!loading && counts.supportRequestsCount > 0 && (
                <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full ml-2">
                  {counts.supportRequestsCount}
                </span>
              )}
            </div>
          </NavLink>
        )}

        {hasPermission('admin.userManagement') && (
          <NavLink
            to="/payouts"
            className={({ isActive }) =>
              `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
            }
          >
            <div className="flex items-center justify-between">
              <span>Payout Requests</span>
              {!loading && counts.payoutRequestsCount > 0 && (
                <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full ml-2">
                  {counts.payoutRequestsCount}
                </span>
              )}
            </div>
          </NavLink>
        )}

        <NavLink
          to="/android-testers"
          className={({ isActive }) =>
            `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
          }
        >
          <div className="flex items-center justify-between">
            <span>Android Testers</span>
            {!loading && counts.androidTestersCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full ml-2">
                {counts.androidTestersCount}
              </span>
            )}
          </div>
        </NavLink>
      </div>

      {/* Tasks Dropdown */}
      {hasPermission('tasks') && (
        <div className="w-full px-4 py-2 mb-2">
          <button
            onClick={() => toggleDropdown('tasks')}
            className="w-full flex items-center justify-between text-xs text-gray-400 uppercase tracking-wide mb-2 hover:text-white transition-colors"
          >
            <span>Tasks</span>
            <span>{openDropdowns.tasks ? '▼' : '▶'}</span>
          </button>
          {openDropdowns.tasks && (
            <div className="ml-2">
              {hasPermission('tasks') && (
                <NavLink
                  to="/tasks"
                  className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
                  }
                >
                  Tasks Overview
                </NavLink>
              )}
              
              {hasPermission('tasks') && (
                <NavLink
                  to="/tasks/categories"
                  className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
              }
            >
                  Categories
                </NavLink>
              )}
              
              {hasPermission('tasks') && (
                <NavLink
                  to="/tasks/subcategories"
                  className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
                  }
                >
                  Subcategories
                </NavLink>
              )}
              
              {hasPermission('tasks') && (
                <NavLink
                  to="/tasks/item-types"
                  className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
                  }
                >
                  Item Types
                </NavLink>
              )}
            </div>
          )}
        </div>
      )}

      {/* App Manager Dropdown */}
      {hasPermission('tasks') && (
        <div className="w-full px-4 py-2 mb-2">
          <button
            onClick={() => toggleDropdown('appManager')}
            className="w-full flex items-center justify-between text-xs text-gray-400 uppercase tracking-wide mb-2 hover:text-white transition-colors"
          >
            <span>App Manager</span>
            <span>{openDropdowns.appManager ? '▼' : '▶'}</span>
          </button>
          {openDropdowns.appManager && (
            <div className="ml-2">
              {hasPermission('tasks') && (
                <NavLink
                  to="/challenges"
                  className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
                  }
                >
                  Challenges
                </NavLink>
              )}
              
              {hasPermission('tasks') && (
                <NavLink
                  to="/contests"
                  className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
                  }
                >
                  Contests & Discounts
                </NavLink>
              )}
            </div>
          )}
        </div>
      )}

      {/* Annotation Manager Dropdown */}
      {(hasPermission('labeling.schemas') || hasPermission('labeling.queue')) && (
        <div className="w-full px-4 py-2 mb-2">
          <button
            onClick={() => toggleDropdown('labelingManager')}
            className="w-full flex items-center justify-between text-xs text-gray-400 uppercase tracking-wide mb-2 hover:text-white transition-colors"
          >
            <span>Annotation Manager</span>
            <span>{openDropdowns.labelingManager ? '▼' : '▶'}</span>
          </button>
          {openDropdowns.labelingManager && (
            <div className="ml-2">
              {hasPermission('labeling.schemas') && (
                <NavLink
                  to="/labeling/schemas"
                  className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
                  }
                >
                  Detection Setup
                </NavLink>
              )}
              {hasPermission('labeling.schemas') && (
                <NavLink
                  to="/labeling/attributes"
                  className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
                  }
                >
                  Attributes
                </NavLink>
              )}
            </div>
          )}
        </div>
      )}

      {/* AI Manager Dropdown */}
      {(hasPermission('labeling.models') || hasPermission('labeling.activeLearning') || 
        hasPermission('labeling.modelQA') || hasPermission('labeling.referenceImages')) && (
        <div className="w-full px-4 py-2 mb-2">
          <button
            onClick={() => toggleDropdown('aiManager')}
            className="w-full flex items-center justify-between text-xs text-gray-400 uppercase tracking-wide mb-2 hover:text-white transition-colors"
          >
            <span>AI Manager</span>
            <span>{openDropdowns.aiManager ? '▼' : '▶'}</span>
          </button>
          {openDropdowns.aiManager && (
            <div className="ml-2">
              {hasPermission('labeling.models') && (
                <NavLink
                  to="/labeling/models"
                  className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
                  }
                >
                  Models
                </NavLink>
              )}
              
              {hasPermission('labeling.datasets') && (
                <NavLink
                  to="/labeling/datasets"
                  className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
                  }
                >
                  Classification Snapshots
                </NavLink>
              )}

              {hasPermission('labeling.datasets') && (
                <NavLink
                  to="/labeling/detection-snapshots"
                  className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
                  }
                >
                  Detection Snapshots
                </NavLink>
              )}
          
          {hasPermission('labeling.activeLearning') && (
            <NavLink
              to="/labeling/active"
              className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
              }
            >
              Active Learning
            </NavLink>
          )}
          
          {hasPermission('labeling.modelQA') && (
            <NavLink
              to="/labeling/model-qa"
              className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
              }
            >
              Model & QA
            </NavLink>
          )}
          
          {hasPermission('labeling.referenceImages') && (
            <NavLink
              to="/reference-images"
              className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
              }
            >
              Reference Images
            </NavLink>
          )}
            </div>
        )}
        </div>
      )}
      
      {/* Data & Analytics Dropdown */}
      {(hasPermission('analytics') || hasPermission('rejectionAnalytics') || hasPermission('userData')) && (
        <div className="w-full px-4 py-2 mb-2">
          <button
            onClick={() => toggleDropdown('dataAnalytics')}
            className="w-full flex items-center justify-between text-xs text-gray-400 uppercase tracking-wide mb-2 hover:text-white transition-colors"
          >
            <span>Data & Analytics</span>
            <span>{openDropdowns.dataAnalytics ? '▼' : '▶'}</span>
          </button>
          {openDropdowns.dataAnalytics && (
            <div className="ml-2">
      {hasPermission('analytics') && (
        <NavLink
          to="/analytics"
          className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
          }
        >
          Task Data Analytics
        </NavLink>
      )}
      
      {hasPermission('rejectionAnalytics') && (
        <NavLink
          to="/rejection-analytics"
          className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
          }
        >
          Rejection Analytics
        </NavLink>
      )}
      {hasPermission('analytics') && (
        <NavLink
          to="/user-data"
          className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
          }
        >
          User Data
        </NavLink>
      )}
            </div>
          )}
        </div>
      )}

      {/* Marketplace Dropdown */}
      {hasPermission('admin.userManagement') && (
        <div className="w-full px-4 py-2 mb-2">
          <button
            onClick={() => toggleDropdown('clientPortal')}
            className="w-full flex items-center justify-between text-xs text-gray-400 uppercase tracking-wide mb-2 hover:text-white transition-colors"
          >
            <span>Marketplace</span>
            <span>{openDropdowns.clientPortal ? '▼' : '▶'}</span>
          </button>
          {openDropdowns.clientPortal && (
            <div className="ml-2">
              <NavLink
                to="/client-portal/packages"
                className={({ isActive }) =>
                  `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${
                    isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"
                  }`
                }
              >
                Credit Packages
              </NavLink>
              <NavLink
                to="/client/dataset-packages"
                className={({ isActive }) =>
                  `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${
                    isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"
                  }`
                }
              >
                Annotated Photos
              </NavLink>
              <NavLink
                to="/client/photo-packages"
                className={({ isActive }) =>
                  `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${
                    isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"
                  }`
                }
              >
                Raw Photos
              </NavLink>
              <NavLink
                to="/client/video-packages"
                className={({ isActive }) =>
                  `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${
                    isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"
                  }`
                }
              >
                Raw Videos
              </NavLink>
              <NavLink
                to="/client/task-pricing"
                className={({ isActive }) =>
                  `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${
                    isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"
                  }`
                }
              >
                Task Pricing
              </NavLink>
            </div>
          )}
        </div>
      )}

      {/* Admin Dropdown */}
      {(hasPermission('admin.workforce') || hasPermission('admin.userManagement')) && (
        <div className="w-full px-4 py-2 mb-2">
          <button
            onClick={() => toggleDropdown('admin')}
            className="w-full flex items-center justify-between text-xs text-gray-400 uppercase tracking-wide mb-2 hover:text-white transition-colors"
          >
            <span>Admin</span>
            <span>{openDropdowns.admin ? '▼' : '▶'}</span>
          </button>
          {openDropdowns.admin && (
            <div className="ml-2">
          {hasPermission('admin.workforce') && (
            <NavLink
              to="/admin/workforce"
              className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
              }
            >
              Workforce
            </NavLink>
          )}
          
          {hasPermission('admin.userManagement') && (
            <NavLink
              to="/admin/user-management"
              className={({ isActive }) =>
                    `block w-full px-4 py-2 mb-1 rounded text-sm transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
              }
            >
              User Management
            </NavLink>
              )}
            </div>
          )}
        </div>
      )}

      {/* User Settings - always show for all users */}
      <NavLink
        to="/user-settings"
        className={({ isActive }) =>
          `w-full px-4 py-2 mb-2 rounded transition-colors ${isActive ? "bg-[#00e3ff] text-black" : "hover:bg-[#00e3ff]/20 text-white"}`
        }
      >
        User Settings
      </NavLink>
    </div>
  );
} 